import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil, filter } from 'rxjs';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { NavigationService } from 'app/core/navigation/navigation.service';
import {constAlert} from "./const.alert";
import {AuthService} from "../../../../core/auth/auth.service";
import {state} from "@angular/animations";
import {fuseAnimations} from "@fuse/animations";
import { ViewTitleService } from "../../../../shared/services/application/header/ViewTitleService"
import { Location } from '@angular/common';
import { SpotlightComponent } from '../../../common/spotlight/spotlight.component';
import { TeoService } from '../../../common/teo/teo.service';
import {StorageLimitAlertService} from "../../../../services/dashboard/storage-limit-alert.service";

@Component({
    selector     : 'classic-layout',
    templateUrl  : './classic.component.html',
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations
})
export class ClassicLayoutComponent implements OnInit, OnDestroy
{
    @ViewChild(SpotlightComponent) spotlight: SpotlightComponent;

    isScreenSmall: boolean;
    showToast: boolean = false;
    navigation: Navigation;
    user: any;
    church: any;
    title: string;
    descTitle: string;
    backButtonVisible: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    protected readonly state = state;

    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _viewTitleService: ViewTitleService,
        private _changeDetectorRef: ChangeDetectorRef,
        private location: Location,
        private _teoService: TeoService,
        private _storageLimitAlertService: StorageLimitAlertService,
    )
    {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number
    {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        this.user = JSON.parse(this._authService.getLSUser);
        this.church = JSON.parse(this._authService.getLSChurch);

        this._viewTitleService.currentTitle.subscribe(title => {
            setTimeout(() => {
                this.title = title;
                this._changeDetectorRef.markForCheck();
            });
        });
        this._viewTitleService.descCurrentTitle.subscribe(descTitle => {
            setTimeout(() => {
                this.descTitle = descTitle;
                this._changeDetectorRef.markForCheck();
            });
        });
        this._viewTitleService.backButtonVisible.subscribe(backButtonVisible => {
            setTimeout(() => {
                this.backButtonVisible = backButtonVisible;
                this._changeDetectorRef.markForCheck();
            });
        });

        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({matchingAliases}) => {

                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Alerta de limite de armazenamento — dirigido por gatilhos de uso:
        // a cada ENTRADA em módulo que grava arquivos no storage (a checagem
        // lê o snapshot em CACHE do StorageUsageService/StorageLimitAlertService,
        // NUNCA re-chama o endpoint). O gatilho pós-login vive no AuthService.
        this._router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                this._storageLimitAlertService.checkOnNavigation(event.urlAfterRedirects);
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void
    {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if ( navigation )
        {
            // Toggle the opened status
            navigation.toggle();
        }
    }


    previousPage(): void
    {
        this.location.back();
    }

    /**
     * Open spotlight
     */
    openSpotlight(): void
    {
        this.spotlight?.open();
    }

    /**
     * Open Teo
     */
    openTeo(): void
    {
        this._teoService.open();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Listen for keyboard shortcuts
     */
    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent): void
    {
        // ALT+T to toggle Teo
        if (event.altKey && event.key.toLowerCase() === 't') {
            event.preventDefault();
            const currentUrl = this._router.url;

            // If already on Teo route, close it
            if (currentUrl.startsWith('/teo')) {
                this._teoService.close();
            } else {
                // Otherwise, open Teo
                this._teoService.open();
            }
        }
    }

    protected readonly constAlert = constAlert;
    protected readonly fuseAnimations = fuseAnimations;
}
